// src/components/table/TblUserSensor.tsx

import {
  Box,
  Button,
  Checkbox,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material'
import { matchSorter } from 'match-sorter'
import { useEffect, useMemo, useState } from 'react'
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'

import { FolderPool } from '@/types/domain/folder'
import { SensorId, SensorPool } from '@/types/domain/sensor'

import {
  CellBoilerplate,
  CellIsActive,
  DefaultColumnFilter,
  FilterStatus,
  HStack,
} from '../'
import {
  getStorage,
  isFilterMatch,
  SORT_ATTR_NAME,
  SVGStatus,
} from '../../api/'
import { useFolderPool, useSensorPool } from '../../hooks/'

type CellStatusProps = {
  row: {
    original: { sensor_target: string; status: string }
  }
}

const CellStatus = ({ row }: CellStatusProps) => {
  const { sensor_target, status } = row.original

  return (
    <Box sx={{ width: 30 }}>
      <SVGStatus type={sensor_target} status={status} />
    </Box>
  )
}

type CellAssignmentProps = {
  row: {
    original: { is_selected: boolean }
  }
}

const CellAssignment = ({ row }: CellAssignmentProps) => {
  const { is_selected } = row.original

  return <Checkbox checked={is_selected} />
}

type Props = {
  user: any
  setUser: (user: any | ((prev: any) => any)) => void
  sensorIdsInFolderPool: SensorId[]
  showActiveOnly?: boolean
  PAGE_CONFIG?: string
}

export const TblUserSensor = ({
  user,
  setUser,
  sensorIdsInFolderPool,
  showActiveOnly = true,
  PAGE_CONFIG = 'settings_user_edit',
}: Props) => {
  const [config, setConfig] = useState(getStorage(PAGE_CONFIG))

  const fPool: FolderPool = useFolderPool()
  const sPool: SensorPool = useSensorPool()

  const { assignedSensorsOnly, filter } = config
  const { sensors } = user

  const [folderSensorsPool, setFolderSensorsPool] = useState([])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { pageIndex, pageSize, filters },
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    pageCount,
  } = useTable(
    {
      columns: useMemo(
        () => [
          {
            accessor: 'entity_id',
            Header: 'Sensor ID',
            sortType: (a, b) =>
              Number(a.original.entity_id) - Number(b.original.entity_id),
          },
          {
            accessor: 'is_active',
            Cell: CellIsActive,
            disableFilters: true,
            disableSortBy: true,
            Header: 'aktiviert',
          },
          { accessor: 'name', Header: 'Name' },
          { accessor: 'device_id', Header: 'Device ID' },
          { accessor: 'folder_name', Cell: CellBoilerplate, Header: 'Gebäude' },
          { accessor: 'sensor_type', Header: 'Typ' },
          { accessor: 'provider', Header: 'Provider' },
          {
            accessor: 'status',
            Cell: CellStatus,
            filterMethod: (filter, row) => {
              if (filter.value === 'all') return true
              if (filter.value === 'green') return row[filter.id] === 'green'
              if (filter.value === 'yellow') return row[filter.id] === 'yellow'

              return row[filter.id] === 'red'
            },
            Filter: FilterStatus,
            Header: 'Status',
          },
          {
            accessor: 'targets',
            Cell: ({ row }) => {
              const { sensor_target, sub_sensor_target } = row.original

              return `${sensor_target}::${sub_sensor_target}`
            },
            disableFilters: true,
            disableSortBy: true,
            Header: 'Targets',
          },
          {
            Cell: CellAssignment,
            disableFilters: true,
            disableSortBy: true,
            Header: 'Zuweisung',
          },
        ],
        []
      ),
      data: useMemo(() => {
        const FILTER_ASSIGNED_SENSORS_ONLY = f =>
          assignedSensorsOnly ? sensors.find(id => id === Number(f.id)) : f

        const FILTER_IS_FOLDER_SENSOR = f =>
          folderSensorsPool.find(id => id === Number(f.id))

        const FILTER_IS_ACTIVE_SENSOR = f =>
          showActiveOnly ? f.attributes.is_active === 1 : f

        const FILTER_SEARCH = f =>
          filter
            ? isFilterMatch(filter, f.attributes.name) ||
              isFilterMatch(filter, f.attributes.device_id) ||
              isFilterMatch(filter, f.id)
            : f

        const result = sPool
          .filter(FILTER_IS_ACTIVE_SENSOR)
          .filter(FILTER_IS_FOLDER_SENSOR)
          .filter(FILTER_ASSIGNED_SENSORS_ONLY)
          .filter(f => sensorIdsInFolderPool.includes(Number(f.id)))
          .filter(FILTER_SEARCH)
          .sort(SORT_ATTR_NAME)
          .map(s => {
            const folder_name =
              fPool.find(f => Number(f.id) === s.attributes.folder_id)
                ?.attributes?.name ?? '-'

            const is_selected = sensors.includes(Number(s.attributes.entity_id))

            return { ...s.attributes, folder_name, is_selected }
          })

        return result
      }, [
        sPool,
        fPool,
        sensorIdsInFolderPool,
        sensors,
        assignedSensorsOnly,
        folderSensorsPool,
        filter,
        showActiveOnly,
      ]),
      defaultColumn: useMemo(() => ({ Filter: DefaultColumnFilter }), []),
      filterTypes: useMemo(
        () => ({
          text: (rows, id, filterValue) =>
            matchSorter(rows, filterValue, { keys: [id] }),

          statusFilter: (rows, id, filterValue) => {
            if (filterValue === 'all') return rows
            return rows.filter(row => row.values[id] === filterValue)
          },
        }),
        []
      ),
      initialState: {
        sortBy: [{ id: config?.orderby, desc: config?.order !== 'ASC' }],
        filters: config.filters ?? [{ id: 'folder_id', value: 'true' }],
        pageSize: config.maxResults ?? 10,
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  const handleRowClick = rowInfo => {
    const { entity_id } = rowInfo.original
    const selected = sensors.includes(Number(entity_id))

    setUser(prev => ({
      ...prev,
      sensors: !selected
        ? [...sensors, Number(entity_id)]
        : sensors.filter(f => f !== Number(entity_id)),
    }))
  }

  useEffect(() => {
    if (!fPool) return

    let pool = fPool.map(f => f.attributes.folder_sensors)
    pool = Object.keys(pool)
      .reduce((arr, key) => arr.concat(pool[key]), [])
      .map(item => item.sensor_id)
      .sort((a, b) => a - b)
    pool = [...new Set(pool)]

    setFolderSensorsPool(pool)
  }, [fPool])

  useEffect(() => {
    setConfig(prev => ({ ...prev, filters }))
  }, [filters])

  return (
    <>
      <TableContainer>
        <Table sx={{ width: 1 }} {...getTableProps()}>
          <TableHead>
            {headerGroups.map(headerGroup => {
              const hg = headerGroup.getHeaderGroupProps()

              return (
                <>
                  <TableRow {...hg}>
                    {headerGroup.headers.map(column => {
                      const hp = column.getHeaderProps(
                        column.getSortByToggleProps()
                      )

                      return (
                        <TableCell key={hp.key} {...hp}>
                          {column.render('Header')}
                        </TableCell>
                      )
                    })}
                  </TableRow>

                  <TableRow>
                    {headerGroup.headers.map(column => {
                      const fhp = column.getHeaderProps()

                      return (
                        <TableCell key={fhp.key} sx={{ p: 0 }} {...fhp}>
                          {column.canFilter ? column.render('Filter') : null}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                </>
              )
            })}
          </TableHead>

          <TableBody {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row)
              const rp = row.getRowProps()

              return (
                <TableRow
                  key={rp.key}
                  onClick={() => handleRowClick(row)}
                  sx={{ cursor: 'pointer' }}
                  {...rp}
                >
                  {row.cells.map(cell => {
                    const cp = cell.getCellProps()

                    return (
                      <TableCell key={cp.key} {...cp}>
                        {cell.render('Cell')}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />

      {/* Pagination Controls */}
      <HStack>
        <Button disabled={pageIndex === 0} onClick={() => previousPage()}>
          Zurück
        </Button>
        <Button
          disabled={pageIndex >= pageCount - 1}
          onClick={() => nextPage()}
        >
          Weiter
        </Button>

        <TablePagination
          component={'div'}
          count={pageCount}
          onPageChange={(event, newPage) => gotoPage(newPage)}
          onRowsPerPageChange={event => setPageSize(Number(event.target.value))}
          page={pageIndex}
          rowsPerPage={Number(pageSize)}
          rowsPerPageOptions={[10, 15, 20, 25, 50, 100]}
        />
      </HStack>
    </>
  )
}
